"use client";

import { useAppStore } from "@/store/useAppStore";
import { badgeSystem } from "@/utils/badgeSystem";
import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { ModalWrapper } from "../ModalWrapper";
import { ShowRewardModal } from "./ShowRewardModal";
import { getGradeEmoji } from "@/helpers/getGrageEmoji";
import { SharingPlatform, TopBias } from "@/app/@types";
import { getInsight } from "@/helpers/getInsight";
import { SharingPlatformModal } from "./SharingPlatformModal";
import { updateUserScore } from "@/lib/user/updateUserScore";
import { useAuth } from "@/hooks/useAuth";
import { addBonusActivity } from "@/lib/activity/addActivityToDB";
import { toast } from "react-toastify";

export const ShareResultsModal = () => {
  const { user } = useAuth();
  const {
    userScore,
    userRank,
    badges,
    setIsModalOpen,
    setActiveModal,
    updateUserPointsScore,
  } = useAppStore();
  const [topBias, setTopBias] = useState<TopBias>("Loss Aversion");
  const accuracy = Math.round(
    (badges.length / Object.keys(badgeSystem).length) * 100
  );

  const [showSharingModal, setShowSharingModal] = useState(false);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [sharingPlatform, setSharingPlatform] = useState<SharingPlatform>(null);
  const storedBonus = localStorage.getItem("probabl_sharing_bonus_earned");
  const cardRef = useRef<HTMLDivElement>(null);
  const generateShareImage = async () => {
    const shareText =
      `🧠 My Probabl Stats\n\n` +
      `🏆 Score     : ${userScore} points\n` +
      `📊 Rank      : ${userRank ? `#${userRank}` : "Unranked"}\n` +
      `🎯 Accuracy  : ${accuracy}%\n` +
      `🧩 Mastered  : ${topBias}\n` +
      `🎖️ Badges   : ${badges.length}\n\n` +
      `⚔️ Think you can beat me?\nPlay now at 👉 probabl.app`;

    return shareText;
  };

  const shareToSocial = async (platform: string) => {
    const text = await generateShareImage();

    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.log("Clipboard write failed, but continuing...");
    }

    switch (platform) {
      case "twitter":
        setSharingPlatform("twitter");
        setShowSharingModal(true);
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
          "_blank"
        );
        break;
      // case "instagram":
      //   setSharingPlatform("instagram");
      //   setShowSharingModal(true);
      //   window.open("https://www.instagram.com/", "_blank");
      //   break;
      // case "tiktok":
      //   setSharingPlatform("tiktok");
      //   setShowSharingModal(true);
      //   window.open("https://www.tiktok.com/", "_blank");
      //   break;
    }
  };

  const handleSharingModalContinue = async () => {
    try {
      if (!storedBonus && user?.email) {
        const startedBonus = 500;
        updateUserPointsScore(startedBonus);

        user?.id && (await updateUserScore(user.id, startedBonus));

        await addBonusActivity(startedBonus);

        setShowRewardModal(true);
        localStorage.setItem("probabl_sharing_bonus_earned", "true");
      } else {
        setShowSharingModal(false);
        setSharingPlatform(null);
      }
    } catch (error) {
      console.log("Error sharing results: ", error);
      toast.error("Something went wrong");
    }
  };

  const handleRewardModalClose = () => {
    setShowRewardModal(false);
  };

  const onClose = () => {
    setIsModalOpen(false);
    setActiveModal(null);
  };
  return (
    <>
      <ModalWrapper>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-md w-full"
        >
          <div
            ref={cardRef}
            className="bg-gradient-to-br from-brand-teal via-brand-amber to-blue-600 p-1 rounded-3xl mb-4"
          >
            <div className="bg-black rounded-3xl p-8">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-white mb-2">
                  🧠 Probabl Master
                </h2>
                <p className="text-gray-300">Behavioral Economics Champion</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-green-400">
                    {userScore}
                  </div>
                  <div className="text-sm text-gray-300">Total Score</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-yellow-400">
                    {userRank ? `#${userRank}` : "—"}
                  </div>
                  <div className="text-sm text-gray-300">Global Rank</div>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-300">Accuracy</span>
                  <span className="text-lg font-bold text-white">
                    {accuracy}% {getGradeEmoji(accuracy)}
                  </span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-3">
                  <motion.div
                    className="h-3 bg-gradient-to-r from-green-400 to-blue-400 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${accuracy}%` }}
                    transition={{ duration: 1, delay: 0.5 }}
                  />
                </div>
              </div>
              <div className="sm:block hidden bg-gradient-to-r from-brand-teal/20 to-brand-amber/20 rounded-xl p-4 mb-6 border border-brand-teal/30">
                <p className="text-center text-white">
                  <span className="text-2xl mb-2 block">🎯</span>
                  <strong>Top Skill:</strong> {topBias} Master
                  <br />
                  <span className="text-sm text-gray-300">
                    {getInsight(topBias)}
                  </span>
                </p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center space-x-2 bg-white/10 rounded-full px-4 py-2">
                  <span className="text-2xl">🏅</span>
                  <span className="text-white font-semibold">
                    {badges.length} Badges Earned
                  </span>
                </div>
              </div>
              <div className="text-center mt-6">
                <p className="text-xs text-gray-400">
                  probabl.app • Beat cognitive biases
                </p>
              </div>
            </div>
          </div>
          {/* <div className="grid grid-cols-3 gap-3 mb-4"> */}
          <div className="flex w-full mb-2">
            <button
              onClick={() => shareToSocial("twitter")}
              className="flex w-full  justify-center align-middle bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-xl font-semibold transition-all cursor pointer"
            >
              🐦 Twitter
            </button>

            {/* <button
              onClick={() => shareToSocial("instagram")}
              className="bg-gradient-to-r from-brand-teal to-brand-amber hover:opacity-90 text-white py-3 rounded-xl font-semibold transition-all"
            >
              📷 Instagram
            </button>
            <button
              onClick={() => shareToSocial("tiktok")}
              className="bg-black hover:bg-gray-900 text-white py-3 rounded-xl font-semibold transition-all border border-white/20"
            >
              🎵 TikTok
            </button> */}
          </div>

          <button
            onClick={onClose}
            className="w-full bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-xl font-semibold transition-all"
          >
            Close
          </button>
        </motion.div>
      </ModalWrapper>

      {showSharingModal && sharingPlatform && (
        <SharingPlatformModal
          sharingPlatform={sharingPlatform}
          handleSharingModalContinue={handleSharingModalContinue}
        />
      )}
      {showRewardModal && (
        <ShowRewardModal handleRewardModalClose={handleRewardModalClose} />
      )}
    </>
  );
};
